import { useState } from 'react';

interface MonthData {
  month: string; // "2026-01"
  invoiced: number;
  collected: number;
}

interface RevenueTrendChartProps {
  data: MonthData[];
}

const money = (n: number) =>
  n >= 1000
    ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
    : `$${n.toFixed(0)}`;

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const RevenueTrendChart = ({ data }: RevenueTrendChartProps) => {
  const [hovered, setHovered] = useState<{ index: number; which: 'invoiced' | 'collected' } | null>(null);

  const maxVal = Math.max(...data.flatMap(d => [d.invoiced, d.collected]), 1);
  // Nice round ceiling for y-axis
  const yMax = Math.ceil(maxVal / 1000) * 1000 || 1000;

  // Chart geometry (in SVG units)
  const W = 560;
  const H = 160;
  const padL = 52;
  const padR = 12;
  const padT = 16;
  const padB = 28;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const groupW = chartW / data.length;
  const barW = Math.min(28, groupW * 0.35);
  const gap = 4;

  // Y-axis ticks
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => ({ frac: t, val: yMax * t }));

  const barX = (i: number, which: 'invoiced' | 'collected') => {
    const center = padL + groupW * i + groupW / 2;
    return which === 'invoiced' ? center - gap / 2 - barW : center + gap / 2;
  };

  const barH = (val: number) => Math.max(2, (val / yMax) * chartH);
  const barY = (val: number) => padT + chartH - barH(val);

  const hov = hovered
    ? { ...data[hovered.index], which: hovered.which }
    : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-gray-900">Revenue Trend</h3>
          <p className="text-xs text-gray-500 mt-0.5">Last 6 months — invoiced vs collected</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-gray-200" />
            Invoiced
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-primary" style={{ backgroundColor: '#C77D3F' }} />
            Collected
          </span>
        </div>
      </div>

      {/* Tooltip */}
      <div className="min-h-[20px] mb-2 text-xs text-gray-600 font-medium">
        {hov ? (
          <span>
            <span className="text-gray-900 font-bold">
              {MONTH_ABBR[parseInt(hov.month.split('-')[1]) - 1]} {hov.month.split('-')[0]}
            </span>
            {' — '}
            {hov.which === 'invoiced' ? 'Invoiced' : 'Collected'}:{' '}
            <span className="font-bold">{new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(hov[hov.which])}</span>
          </span>
        ) : null}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        style={{ height: '160px' }}
        onMouseLeave={() => setHovered(null)}
      >
        {/* Y-axis gridlines + labels */}
        {yTicks.map(({ frac, val }) => {
          const y = padT + chartH * (1 - frac);
          return (
            <g key={frac}>
              <line x1={padL} y1={y} x2={W - padR} y2={y}
                stroke="#e5e7eb" strokeWidth="1" strokeDasharray={frac === 0 ? '' : '3 3'} />
              <text x={padL - 6} y={y + 4} textAnchor="end"
                fontSize="10" fill="#9ca3af">{money(val)}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const ix = barX(i, 'invoiced');
          const cx = barX(i, 'collected');
          const ih = barH(d.invoiced);
          const ch = barH(d.collected);
          const iy = barY(d.invoiced);
          const cy = barY(d.collected);
          const hovI = hovered?.index === i && hovered.which === 'invoiced';
          const hovC = hovered?.index === i && hovered.which === 'collected';

          return (
            <g key={d.month}>
              {/* Invoiced bar */}
              <rect
                x={ix} y={iy} width={barW} height={ih}
                rx="3"
                fill={hovI ? '#d1d5db' : '#e5e7eb'}
                className="cursor-pointer transition-colors"
                onMouseEnter={() => setHovered({ index: i, which: 'invoiced' })}
              />
              {/* Collected bar */}
              <rect
                x={cx} y={cy} width={barW} height={ch}
                rx="3"
                fill={hovC ? '#b36a2e' : '#C77D3F'}
                className="cursor-pointer transition-colors"
                onMouseEnter={() => setHovered({ index: i, which: 'collected' })}
              />
              {/* X-axis label */}
              <text
                x={padL + groupW * i + groupW / 2} y={H - 6}
                textAnchor="middle" fontSize="10" fill="#9ca3af"
              >
                {MONTH_ABBR[parseInt(d.month.split('-')[1]) - 1]}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};
